import { useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';

export type ChatTriggerType = 'idle' | 'back-button' | 'vas-uncertainty' | 'product-uncertainty' | 'form-error';

// Module-level state that persists across component unmount/mount during navigation
let pendingBackNavigation = { detected: false, wasEnabled: false };

interface UseChatTriggersProps {
  enabled: boolean;
  triggerCallback: (type: ChatTriggerType) => void;
  idleTimeout?: number; // in seconds
  backButtonEnabled?: boolean; // if falsy, back button trigger will be disabled
}

/**
 * In case too many triggers are added, we can create a better method to handle the trigger logic.
 * Fast follow to be created to check if there is a better method to handle the trigger logic.
 **/
function shouldTriggerChat(type: ChatTriggerType, triggerProps: UseChatTriggersProps) {
  const { backButtonEnabled, enabled } = triggerProps;

  if (!enabled) {
    return false;
  }

  if (type === 'back-button' && backButtonEnabled !== true) {
    return false;
  }

  return true;
}

//Sets up automatic chat triggers and returns a manual trigger function
export const useChatTriggers = (props: UseChatTriggersProps): ((type: ChatTriggerType) => void) => {
  const { enabled, triggerCallback } = props;

  const router = useRouter();
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const enabledRef = useRef(enabled);

  // Keep enabledRef in sync with enabled prop
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Core trigger function that updates state and shows chatbot
  const triggerChat = useCallback(
    (type: ChatTriggerType) => {
      const shouldTrigger = shouldTriggerChat(type, props);
      console.debug("Chat was triggered by", type, " with following props", props);

      if (shouldTrigger) {
        triggerCallback(type);
      }
    },
    [enabled, triggerCallback]
  );

  // --- Idle Trigger ---
  useEffect(() => {
    if (!enabled || props.idleTimeout === undefined) return;

    const timeoutInMs = props.idleTimeout * 1000;

    const resetTimer = () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => {
        triggerChat('idle');
      }, timeoutInMs); 
    };

    const activityEvents = ['mousemove', 'keydown', 'scroll', 'click'];
    activityEvents.forEach((e) => document.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      activityEvents.forEach((e) => document.removeEventListener(e, resetTimer));
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    };
  }, [enabled, props.idleTimeout, triggerChat]);

  // --- Back Button Trigger: Track via Next.js router ---
  useEffect(() => {
    if (!router) return;

    const handleBackNavigation = () => {
      const wasEnabled = enabledRef.current;
      // Use module-level variable so it persists across component unmount/mount
      pendingBackNavigation = { detected: true, wasEnabled };
      return true; // Allow navigation to proceed
    };

    router.beforePopState(handleBackNavigation);

    return () => {
      router.beforePopState(() => true); // Reset to default
    };
  }, [router]);

  // --- Back Button Trigger: Fire AFTER navigation completes ---
  useEffect(() => {
    if (!router?.events) return;
    const handleRouteChangeComplete = () => {
      // Check module-level variable which persists across component instances
      if (pendingBackNavigation.detected && pendingBackNavigation.wasEnabled) {
        triggerChat('back-button');
        // Reset the module-level variable
        pendingBackNavigation = { detected: false, wasEnabled: false };
      }
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [triggerChat, router]);

  // --- Native popstate fallback for non-Next.js navigation ---
  useEffect(() => {
    const handlePopState = () => {
      // Only trigger if Next.js router didn't handle it AND was enabled at the time
      if (!pendingBackNavigation.detected) {
        const wasEnabled = enabledRef.current;
        if (wasEnabled) {
          triggerChat('back-button');
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [triggerChat]);

  // --- Cart Removal Trigger: setup manually ---
  // --- Form Error Trigger: setup manually ---

  // Return the manual trigger function
  return triggerChat;
};
